import React, { Component } from 'react';
import { connectDeep } from 'redux-utils';
import PropTypes from 'prop-types';
import RFRLink from 'redux-first-router-link';
import styled from 'styled-components';
import raf from 'raf';
import GearIcon from 'icons/gear.svg';
import store from 'store';
import { direction } from 'actions/gallery';
import ScrollContainer from 'redux-first-router-scroll-container';
import { isiOS } from 'config';

export const FULL_VH = document.documentElement.clientHeight;
export const FULL_VW = document.documentElement.clientWidth;

const INSTALLED_TYPES = ['APPS', 'CLOCKS'];
const DISCOVERY_TYPES = ['APPS_GALLERY', 'CLOCKS_GALLERY'];

const shouldUpdateScroll = (prev, next) => {
  if (
    prev &&
    next &&
    ((INSTALLED_TYPES.includes(prev.type) &&
      DISCOVERY_TYPES.includes(next.type)) ||
      (DISCOVERY_TYPES.includes(prev.type) &&
        INSTALLED_TYPES.includes(next.type)))
  ) {
    return [0, 0];
  }

  // Don't update scroll if we are pushing a new page onto the history stack
  if (!next || next.kind === 'push') {
    return false;
  }

  // Otherwise, if popping (forward/back), update scroll
  return true;
};

export const BasicContentBlock = styled.div`
  ${({ theme, borderTop, borderBottom }) => `
    background-color: white;
    padding-left: ${theme.edgeDistance};
    padding-right: ${theme.edgeDistance};

    ${
      isiOS
        ? `
      ${borderTop ? `border-top: 1px solid ${theme.borderColor};` : ''}
      ${borderBottom ? `border-bottom: 1px solid ${theme.borderColor};` : ''}
    `
        : ''
    }
  `}
`;

export const ContentBlock = styled(BasicContentBlock)`
  padding-top: 10px;
  padding-bottom: 10px;

  font-size: 16px;
  line-height: 24px;
`;

export const Bold = styled.span`
  font-family: ${({ theme }) => theme.fontBold};
`;

export const Center = styled.div`
  text-align: center;
`;

export const Icon = `
  height: auto;
  width: 48px;
`;

export const SectionTitle = styled.h2`
  ${({ theme }) => `
    font: 14px ${theme.fontSemibold};
    margin: 0;
    margin-top: 20px;
    padding-left: ${theme.edgeDistance};

    ${
      isiOS
        ? `
      color: #767676;
      margin-bottom: 5px;
      margin-top: 35px;
      text-transform: uppercase;
    `
        : `
      color: ${theme.fitbitGreen};
      margin-bottom: 6px;
    `
    }
  `}
`;

export const SettingsIcon = styled.div`
  background-image: url(${GearIcon});
  background-size: contain;
  cursor: pointer;
  width: 22px;
  height: 22px;
  -webkit-transform: translateZ(0);
`;

export const Screen = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.backgroundColor};
  z-index: 5000;
  ${({ theme }) => theme.Screen}
`;

export const StyledRFRLink = styled(RFRLink)`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

export class Link extends Component {
  static propTypes = {
    pageDirection: PropTypes.string,
    onClick: PropTypes.func
  };

  onClick = () => {
    const { pageDirection, onClick } = this.props;
    store.dispatch(direction(pageDirection));
    if (onClick) {
      onClick();
    }
  };

  render() {
    // eslint-disable-next-line
    const { children, pageDirection, ...rfrLinkProps } = this.props;
    return (
      <StyledRFRLink {...rfrLinkProps} onClick={this.onClick}>
        {children}
      </StyledRFRLink>
    );
  }
}

export const animationFixStyle = `
  -webkit-backface-visibility: hidden;
  -webkit-transform-style: preserve-3d;
`;

const scrollThreshold = 10;

const DisconnectedPage = styled(
  class extends Component {
    onRef = (ref) => {
      this.ref = ref;
      if (this.ref) {
        this.ref.addEventListener('scroll', this.handleScroll);
        this.ref.addEventListener('touchmove', this.handleScroll);
      }
    };

    handleScroll = () => {
      this.rafHandle = raf(() => {
        this.rafHandle = null;
        if (!this.ref) {
          return;
        }

        const withinThreshold =
          this.ref.scrollTop > scrollThreshold &&
          this.ref.scrollTop < this.ref.clientHeight - scrollThreshold;
        let top = this.ref.scrollTop;
        if (this.ref.scrollTop <= scrollThreshold) {
          top = 0;
        } else if (
          this.ref.scrollTop >=
          this.ref.clientHeight - scrollThreshold
        ) {
          top = this.ref.clientHeight;
        }
        this.props.onScroll({
          scrolling: withinThreshold,
          top
        });
      });
    };

    componentWillUmmount() {
      this.ref.removeEventListener('scroll', this.handleScroll);
      this.ref.removeEventListener('touchmove', this.handleScroll);

      if (this.rafHandle) {
        this.rafHandle.cancel();
        this.rafHandle = null;
      }
    }

    render() {
      const { className, children, scrollKey } = this.props;

      const scrollEl = (
        <div ref={this.onRef} className={className}>
          {children}
        </div>
      );

      return scrollKey ? (
        <ScrollContainer
          scrollKey={scrollKey}
          shouldUpdateScroll={shouldUpdateScroll}
        >
          {scrollEl}
        </ScrollContainer>
      ) : (
        scrollEl
      );
    }
  }
)`
  box-sizing: border-box;
  height: calc(${FULL_VH}px - ${({ navbarHeight }) => navbarHeight});
  margin-top: ${({ navbarHeight }) => navbarHeight};
  position: relative;
  // Force GPU Accel
  transform: translate3d(0, 0, 0);
  ${({ modalShowing = false, theme }) => `
    overflow-y: ${isiOS && modalShowing ? 'hidden' : 'auto'};
    overflow-x: hidden;
    ${theme.Page}
  `}
`;

const mapStateToProps = ({ gallery: { modalShowing } }) => ({
  modalShowing
});

export const Page = connectDeep(mapStateToProps)(DisconnectedPage);
